import { useQuery } from '@apollo/client';
import { getDataFromTree } from '@apollo/client/react/ssr';
import { NextPage } from 'next';
import { ApolloPageContext, PageProps } from 'helpers/next-page';
// Api
import { GET_ALL_MERCH_PRODUCTS } from 'api/allMerch/queries';
// Types
import {
  GetAllMerchProducts,
  GetAllMerchProductsVariables,
} from 'api/allMerch/types/GetAllMerchProducts';
import { MerchProductOrderBy, SortDirection } from 'api/graphql-global-types';
// Helpers
import { getAthleteTabMetaTitle } from 'helpers/athleteTabSeo';
import { getAthleteTabPageProps } from 'helpers/athleteTabPageGuard';
import { getEnvLink } from 'helpers/routes';
import { generateItemListStructuredData } from 'helpers/structured-data';
// Hocks
import withApollo from 'hocs/withApollo';
// Hooks
import {
  useRedirectIfNoContent,
  useRedirectOnError,
  useAthletePageData,
} from 'hooks';
// Context
import { FiltersProvider } from 'components/common3/PageWithFiltersWrapper/PageWithFiltersContext';
// Layouts
import { getProfileAvatarPreload } from 'helpers/profileAvatar';
import Head from 'layouts/Head/Head';
import Header from 'layouts/Header/Header';
import Main from 'layouts/Main/Main';
import Footer from 'layouts/Footer/Footer';
// Components
import AppBannerAndModal from 'components/common3/AppBannerAndModal/AppBannerAndModal';
import Description from 'components/Athlete/Description/Description';
import AllMerchContainer from 'components/AllMerch/AllMerch';
import NotFoundPage from 'components/NotFoundPage/NotFoundPage';

const MerchContent = (): JSX.Element | null => {
  const {
    error,
    cleanedSlug,
    athleteSlug,
    metaData,
    store,
  } = useAthletePageData();

  const { data: merchData } = useQuery<
    GetAllMerchProducts,
    GetAllMerchProductsVariables
  >(GET_ALL_MERCH_PRODUCTS, {
    variables: {
      input: {
        storeId: store?.id || '',
      },
    },
    skip: !store,
  });

  const merch = merchData?.getMerchProducts?.entities;

  useRedirectOnError({ error, redirectTo: `/${athleteSlug}` });

  useRedirectIfNoContent({
    hasContent: store?.hasMerch ?? false,
    redirectTo: `/${athleteSlug}`,
    skip: !store,
  });

  if (!store) {
    return null;
  }

  const canonicalLink = `${getEnvLink()}/${cleanedSlug}/merch`;

  const merchItems =
    merch?.map((item) => ({
      url: `${getEnvLink()}/${item.store.slug}/merch/${item.slug}`,
    })) || [];

  const structuredData = generateItemListStructuredData(
    `${metaData.name} Merchandise`,
    merchItems
  );

  return (
    <>
      <Head
        title={getAthleteTabMetaTitle(metaData.name, 'merch')}
        description={`Browse official ${metaData.name} merchandise. T-shirts, hoodies, hats and more available on MILLIONS.`}
        image={metaData.avatarUrl}
        imagePreload={getProfileAvatarPreload(metaData.avatarUrl)}
        canonicalLink={canonicalLink}
        robots={store.hasMerch ? 'index, follow' : 'noindex, follow'}
        structuredData={structuredData}
      />
      <AppBannerAndModal
        when
        variant="profile"
        username={cleanedSlug ?? undefined}
        profileImageUrl={metaData.avatarUrl ?? undefined}
      />
      <Header />

      <Main backgroundColor="background-dim" withoutSidesPadding>
        <Description store={store} />
        {store.hasMerch && (
          <FiltersProvider
            storesListFilter="hasActiveMerch"
            searchKey="searchTerm"
            hideContentCreatorsFilter
            hideSports
            hideAthletesFilter
            hideOrganizationsFilter
            hasAthleteSlug
            initialSorting={{
              direction: SortDirection.DESC,
              order: MerchProductOrderBy.Date,
            }}
          >
            <AllMerchContainer
              isPage={false}
              storeSlug={cleanedSlug as string | undefined}
              singleCategory
            />
          </FiltersProvider>
        )}
      </Main>

      <Footer />
    </>
  );
};

const Merch: NextPage<PageProps> = ({ isNotFound }) =>
  isNotFound ? <NotFoundPage /> : <MerchContent />;

Merch.getInitialProps = async (ctx: ApolloPageContext): Promise<PageProps> =>
  getAthleteTabPageProps(ctx, 'merch');

export default withApollo(Merch, { getDataFromTree });
